import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import Breadcrumb from '../components/common/Breadcrumb';
import AuthorCard from '../components/common/AuthorCard';
import RecentArticles from '../components/blog/RecentArticles';
import WhySunServ from '../components/blog/WhySunServ';
import DiscordCard from '../components/blog/DiscordCard';
import ServerPromoCard from '../components/blog/ServerPromoCard';
import ContributorCard from '../components/blog/ContributorCard';
import api from '../utils/api';
import { formatDate } from '../utils/helpers';
import toast from 'react-hot-toast';

const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [suggested, setSuggested] = useState([]);
  const [recentArticles, setRecentArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticle();
    loadRecentArticles();
    window.scrollTo(0, 0);
  }, [slug]);

  const loadArticle = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/articles/${slug}`);
      if (response.data.success) {
        setArticle(response.data.article);
        setSuggested(response.data.suggested || []);
      }
    } catch (error) {
      console.error('Erreur chargement article:', error);
      toast.error('Article non trouvé');
    } finally {
      setLoading(false);
    }
  };

  const loadRecentArticles = async () => {
    try {
      const response = await api.get('/articles?limit=5');
      if (response.data.success) {
        setRecentArticles(response.data.articles.slice(0, 5));
      }
    } catch (error) {
      console.error('Erreur chargement articles récents:', error);
    }
  };

  // Ajoute loading="lazy" + decoding="async" à toutes les <img> du HTML
  // injecté pour que les images en bas d'article ne bloquent pas le rendu.
  const enhancedContent = useMemo(() => {
    if (!article?.content) return '';
    return article.content.replace(/<img(?![^>]*\bloading=)([^>]*)>/gi, '<img loading="lazy" decoding="async"$1>');
  }, [article?.content]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background pt-24 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-400 mt-4">Chargement de l'article...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!article) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background pt-24 flex items-center justify-center">
          <div className="text-center">
            <i className="fas fa-exclamation-triangle text-6xl text-yellow-400 mb-4"></i>
            <h1 className="text-3xl font-bold text-white mb-2">Article non trouvé</h1>
            <p className="text-gray-400 mb-6">L'article que vous recherchez n'existe pas ou a été supprimé.</p>
            <Link to="/" className="bg-yellow-400 text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-yellow-500 transition-colors">
              Retour au blog
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{article.meta_title || article.title} - Blog SunServ</title>
        <meta name="description" content={article.meta_description || article.short_description} />
        {article.meta_keywords && <meta name="keywords" content={article.meta_keywords} />}
        
        {/* Open Graph */}
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.short_description} />
        {article.featured_image && <meta property="og:image" content={article.featured_image} />}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.short_description} />
        {article.featured_image && <meta name="twitter:image" content={article.featured_image} />}
        
        {/* Article Meta */}
        <meta name="author" content={article.author_name} />
        {article.published_at && <meta property="article:published_time" content={article.published_at} />}
        {article.category_name && <meta property="article:section" content={article.category_name} />}
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": article.title,
            "description": article.short_description,
            "image": article.featured_image,
            "datePublished": article.published_at,
            "author": {
              "@type": "Person",
              "name": article.author_name
            },
            "publisher": {
              "@type": "Organization",
              "name": "SunServ",
              "logo": {
                "@type": "ImageObject",
                "url": "https://sunserv.fr/logo.png"
              }
            }
          })}
        </script>
      </Helmet>

      <Header />

      <div className="min-h-screen bg-background pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Breadcrumb */}
          <Breadcrumb 
            items={[
              { label: 'Accueil', path: '/' },
              { label: 'Blog', path: '/' },
              ...(article.category_name ? [{ label: article.category_name, path: `/category/${article.category_slug}` }] : []),
              { label: article.title }
            ]}
          />

          {/* Titre centré */}
          <div className="text-center max-w-4xl mx-auto mb-12">
            {article.category_name && (
              <Link
                to={`/category/${article.category_slug}`}
                className="inline-block px-4 py-1 rounded-full text-sm font-medium text-gray-900 mb-4"
                style={{ backgroundColor: article.category_color }}
              >
                {article.category_name}
              </Link>
            )}
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {article.title}
            </h1>

            {article.short_description && (
              <p className="text-xl text-gray-300 mb-8">
                {article.short_description}
              </p>
            )}

            <div className="flex items-center justify-center gap-6 text-gray-400 text-sm">
              <span>
                <i className="fas fa-calendar mr-2"></i>
                {formatDate(article.published_at)}
              </span>
              {article.reading_time && (
                <span>
                  <i className="fas fa-clock mr-2"></i>
                  {article.reading_time} min de lecture
                </span>
              )}
              <span>
                <i className="fas fa-eye mr-2"></i>
                {article.views} vues
              </span>
            </div>
          </div>

          {/* AuthorCard */}
          <div className="max-w-4xl mx-auto mb-12">
            <AuthorCard 
              author={{
                id: article.author_id,
                username: article.author_name,
                avatar: article.author_avatar,
                bio: article.author_bio,
                tags: article.author_tags || [],
                article_count: article.author_article_count || 0
              }}
              article={article}
            />
          </div>

          {/* Image principale */}
          {article.featured_image && (
            <div className="max-w-4xl mx-auto mb-12">
              <img
                src={article.featured_image}
                alt={article.title}
                loading="eager"
                decoding="async"
                fetchpriority="high"
                className="w-full h-auto rounded-2xl object-cover shadow-2xl"
                style={{ maxHeight: '600px' }}
              />
            </div>
          )}

          {/* ServerPromoCard avant le contenu */}
          <div className="max-w-4xl mx-auto mb-12">
            <ServerPromoCard />
          </div>

          {/* Contenu avec sidebar */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contenu principal */}
            <div className="lg:col-span-2">
              <div
                className="article-content bg-surface rounded-xl p-8 text-gray-200 shadow-lg"
                dangerouslySetInnerHTML={{ __html: enhancedContent }}
              />
            </div>

            {/* Sidebar */}
            <div className="lg:sticky lg:top-24 lg:self-start">
              <div className="space-y-6">
                <RecentArticles articles={recentArticles} />
                <WhySunServ />
                <DiscordCard />
              </div>
            </div>
          </div>

          {/* ServerPromoCard après le contenu */}
          <div className="max-w-4xl mx-auto mt-12">
            <ServerPromoCard />
          </div>

          {/* ContributorCard */}
          <div className="max-w-5xl mx-auto mt-12">
            <ContributorCard />
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default ArticlePage;
